import * as culture from "client_legacy/ndaw/culture";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { calendarLogic } from "./calendarLogic";
import {
	union,
	unique,
	without,
	sortNumbers
} from "client_legacy/ndaw/arrayHelpers";
import {
	IInterestsMerger,
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import {
	AppointmentsFilter,
	AppointmentsLoadStatus,
	AppointmentsResult,
	AvailableCalendarModel,
	getAppointments,
	getAvailableCalendars,
	listPreferences,
	setPreference
} from "client/Api/Workspace/LiveInformation/Calendar/calendarLiveInformation";
import {
	wrapPromiseWithCancellation,
	ensureNotNil,
	isNotNil
} from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	addDays,
	calculateDayDifference,
	formatForDto
} from "client/DateAndTime/dateTimeUtils";
import { userProfileStore } from "client/Features/Settings/UserProfile/userProfileStore";
import { AppointmentViewModel } from "client/Api/Calendar/calendar";

interface AppointmentsLoadingError {
	isError: true;
	errorMessage: string;
}

export type AppointmentItemModel = AppointmentViewModel & {
	uniqueId: Guid | string;
};

export interface AppointmentsLoaded {
	isError: false;
	appointments: readonly AppointmentItemModel[];
}

export type AppointmentsLoadResult = AppointmentsLoadingError | AppointmentsLoaded;

const ExchangeNotConfiguredAppointmentsResult: AppointmentsLoadingError = {
	isError: true,
	errorMessage: culture.localize("_TheConnectionWithExchangeIsNotConfiguredThereforeWeCouldNotRetrieveYourAppointments")
};

const AccessDeniedAppointmentsResult: AppointmentsLoadingError = {
	isError: true,
	errorMessage: culture.localize("_NoPermissionsToReadCalendar")
};

const UnknownErrorAppointmentsResult: AppointmentsLoadingError = {
	isError: true,
	errorMessage: culture.localize("_ErrorDuringAppointmentRetrieval")
};

interface CalendarLiveInformationProviderOptions {
	intervalInSeconds: number;
}

export type DateAsNumber = number;

interface SpecificCalendarCache {
	upcomingAppointments?: AppointmentsLoadResult;
	appointmentsByDates?: Map<DateAsNumber, AppointmentsLoadResult>;
}

interface DateSpan {
	start: DateAsNumber;
	end: DateAsNumber;
}

export type CalendarResourceId = string;

export type CalendarInterests = {
	upcoming?: boolean;
	dates?: readonly DateAsNumber[];
};

export type AppointmentsUpdate = {
	isError: true;
	errorMessage: string;
} | {
	isError: false;
} & ({
	appointmentsByDates: Map<DateAsNumber, readonly AppointmentItemModel[]>;
	upcomingAppointments?: undefined;
} | {
	upcomingAppointments: readonly AppointmentItemModel[];
	appointmentsByDates?: undefined;
});
export interface CalendarInterestedParty extends InterestedParty<Guid, CalendarResourceId> {
	update(appointments: AppointmentsUpdate): void;
}

export class CalendarLiveInformationProvider extends LiveInformationProvider<
	CalendarLiveInformationProviderOptions,
	Guid,
	CalendarResourceId,
	CalendarInterests,
	CalendarInterestedParty,
	SpecificCalendarCache
>
{
	private _listRetrievalPromise: Promise<readonly AvailableCalendarModel[]> | undefined;
	private readonly _preferences: Map<number, string>;
	private _preferenceRetrievalPromise: Promise<void> | undefined;

	private readonly _isExchangeConfigured: boolean;
	private readonly _isExchangeEnabled: boolean;

	public get isExchangeEnabled(): boolean {
		return this._isExchangeEnabled;
	}

	public get isExchangeAvailable(): boolean {
		return this._isExchangeConfigured && this._isExchangeEnabled;
	}

	constructor(options: CalendarLiveInformationProviderOptions) {
		super(options);

		const { exchange } = userConfiguration();
		this._isExchangeEnabled = exchange.isEnabled;
		this._isExchangeConfigured = exchange.isConfigured;
		this._preferences = new Map<number, string>();
	}

	public listCalendars(signal?: AbortSignal): Promise<readonly AvailableCalendarModel[]>
	{
		if (!this._listRetrievalPromise) {
			// The main promise we never want to cancel as it is shared between all tiles
			this._listRetrievalPromise = getAvailableCalendars();
		}

		return wrapPromiseWithCancellation(
			this._listRetrievalPromise,
			signal
		);
	}

	public async getPreference(tileId: number, signal?: AbortSignal): Promise<string | undefined>
	{
		if (!this._preferences.has(tileId)) {
			await this.loadPreferencesAsync(signal);
		}

		return this._preferences.get(tileId);
	}

	public setPreference(
		tileId: number,
		calendarId: string,
		signal?: AbortSignal
	): Promise<void> {
		this._preferences.set(tileId, calendarId);

		return setPreference(
			tileId,
			{ calendarId },
			signal
		);
	}

	protected async getDataFromBackend(
		entries: readonly [CalendarResourceId, CalendarInterests][]
	): Promise<readonly [CalendarResourceId, SpecificCalendarCache][]> {
		if (!this.isExchangeAvailable) {
			return Promise.reject();
		}

		const { userTimezone } = userProfileStore.getState();

		const filters: AppointmentsFilter[] = entries.map(
			([resourceId, interests]) => {
				const dateSpans: DateSpan[] = this.getSpansFromDates(interests.dates || [], userTimezone);
				return {
					id: resourceId,
					upcomingAppointments: interests.upcoming ?? false,
					customDateSpans: dateSpans.map(s => ({
						start: formatForDto(new Date(s.start), userTimezone),
						end: formatForDto(new Date(s.end), userTimezone)
					}))
				};
			}
		);

		const backendResults: AppointmentsResult[] = await getAppointments(filters);

		return this.convertBackendResults(
			entries,
			backendResults,
			userTimezone
		);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<CalendarInterests, SpecificCalendarCache>,
		response: [CalendarInterests, SpecificCalendarCache]
	): void {

		const [retrievedInterests, model] = response;

		if (retrievedInterests.upcoming) {
			cacheItem.retrievedInterests.upcoming = true;
			cacheItem.data.upcomingAppointments = model.upcomingAppointments;
		}

		if (retrievedInterests.dates) {
			const cachedDates: DateAsNumber[] = [...(cacheItem.retrievedInterests.dates ?? [])];
			for (const date of retrievedInterests.dates) {
				if (!cachedDates.some(x => x === date)) {
					cachedDates.push(date);
				}
			}

			const appointmentsByDates: Map<DateAsNumber, AppointmentsLoadResult> = cacheItem.data
				.appointmentsByDates ?? new Map<DateAsNumber, AppointmentsLoadResult>();

			for (const [date, loadingResult] of ensureNotNil(model.appointmentsByDates).entries()) {
				appointmentsByDates.set(date, loadingResult);
			}

			cacheItem.retrievedInterests.dates = cachedDates;
			cacheItem.data.appointmentsByDates = appointmentsByDates;
		}
	}

	protected override informAboutGlobalRetrievalFailure(party: CalendarInterestedParty): void {
		if (!this.isExchangeAvailable) {
			party.update(ExchangeNotConfiguredAppointmentsResult);
			return;
		}

		super.informAboutGlobalRetrievalFailure(party);
	}

	protected informParty(
		party: CalendarInterestedParty,
		cacheItem: [CalendarInterests, SpecificCalendarCache]
	): void {
		if (!this.isExchangeAvailable) {
			party.update(ExchangeNotConfiguredAppointmentsResult);
			return;
		}

		const [retrievedInterests, response] = cacheItem;
		const interestedIn: CalendarInterests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.upcoming && retrievedInterests.upcoming) {
			if (response.upcomingAppointments?.isError) {
				party.update(response.upcomingAppointments);
			} else {
				const upcomingAppointments: readonly AppointmentItemModel[] = ensureNotNil(response.upcomingAppointments).appointments;

				party.update({
					isError: false,
					upcomingAppointments
				});
			}
		}

		if (interestedIn.dates && retrievedInterests.dates) {
			let errorMessage: string | undefined;
			const appointmentsByDates = new Map<DateAsNumber, readonly AppointmentItemModel[]>();

			for (const date of interestedIn.dates) {
				const result: AppointmentsLoadResult | undefined = ensureNotNil(response.appointmentsByDates)
					.get(date);

				if (!result) {
					continue;
				}

				if (result.isError === true) {
					errorMessage = result.errorMessage;
					break;
				}

				appointmentsByDates.set(date, result.appointments);

				if (errorMessage) {
					break;
				}
			}

			if (errorMessage) {
				party.update({
					isError: true,
					errorMessage
				});
			} else {
				party.update({
					isError: false,
					appointmentsByDates
				});
			}
		}
	}

	private getSpansFromDates(dates: readonly DateAsNumber[], timezone: TimeZone): DateSpan[] {
		const uniqueDates: readonly DateAsNumber[] = unique(sortNumbers(dates));
		let previousSpan: DateSpan | null = null;
		const resultSpans: DateSpan[] = [];

		for (const date of uniqueDates) {
			const dateToLoad = new Date(date);
			if (previousSpan && calculateDayDifference(dateToLoad, new Date(previousSpan.end), timezone, true) <= 1) {
				previousSpan.end = addDays(dateToLoad, 1, timezone).getTime();
			} else {
				if (previousSpan) {
					resultSpans.push(previousSpan);
				}

				previousSpan = {
					start: dateToLoad.getTime(),
					end: addDays(dateToLoad, 1, timezone).getTime()
				};
			}
		}
		if (previousSpan) {
			resultSpans.push(previousSpan);
		}

		return resultSpans;
	}

	private convertBackendResults(
		entries: readonly [CalendarResourceId, CalendarInterests][],
		backendResults: readonly AppointmentsResult[],
		timezone: TimeZone
	): readonly [CalendarResourceId, SpecificCalendarCache][]
	{
		const backendResultsMap: Map<CalendarResourceId, AppointmentsResult> = this.createResourceMap<AppointmentsResult>();
		for (const backendResult of backendResults) {
			const resourceId: CalendarResourceId = backendResult.id;
			backendResultsMap.set(resourceId, backendResult);
		}

		const result: [CalendarResourceId, SpecificCalendarCache][] = [];

		for (const [resourceId, interests] of entries)
		{
			const backendResult: AppointmentsResult | undefined = backendResultsMap.get(resourceId);

			const item: SpecificCalendarCache = {};

			if (interests.upcoming) {
				if (!backendResult) {
					item.upcomingAppointments = UnknownErrorAppointmentsResult;
				} else {
					switch (backendResult.status) {
						case AppointmentsLoadStatus.NoError:
							item.upcomingAppointments = {
								isError: false,
								appointments: backendResult
									.upcomingAppointments
									.map(convertToItemModel)
							};
							break;
						case AppointmentsLoadStatus.AccessDenied:
							item.upcomingAppointments = AccessDeniedAppointmentsResult;
							break;
						case AppointmentsLoadStatus.UnknownError:
							item.upcomingAppointments = UnknownErrorAppointmentsResult;
							break;
						default:
							throw new UnreachableCaseError(backendResult);
					}
				}
			}

			if (interests.dates) {
				item.appointmentsByDates = new Map<DateAsNumber, AppointmentsLoadResult>();
				for (const day of interests.dates) {
					if (!backendResult) {
						item.appointmentsByDates.set(day, UnknownErrorAppointmentsResult);
					} else {
						switch (backendResult.status) {
							case AppointmentsLoadStatus.NoError:
								item.appointmentsByDates.set(day, {
									isError: false,
									appointments: calendarLogic.getAppointmentsPerDay(
										backendResult.appointmentsFromRequestedSpans
											.map(convertToItemModel),
										new Date(day),
										timezone
									)
								});
								break;
							case AppointmentsLoadStatus.AccessDenied:
								item.appointmentsByDates.set(day, AccessDeniedAppointmentsResult);
								break;
							case AppointmentsLoadStatus.UnknownError:
								item.appointmentsByDates.set(day, UnknownErrorAppointmentsResult);
								break;
							default:
								throw new UnreachableCaseError(backendResult);
						}
					}
				}
			}

			result.push([resourceId, item]);

		}

		return result;
	}

	protected override createInterestsMerger(): IInterestsMerger<CalendarResourceId, CalendarInterests> {
		return new CalendarInterestsMerger(this.createResourceMap());
	}

	private loadPreferencesAsync(signal?: AbortSignal): Promise<void>
	{
		if (!this._preferenceRetrievalPromise) {
			// The main promise we never want to cancel as it is shared between all tiles
			this._preferenceRetrievalPromise = listPreferences()
				.then(
					(x): void => {
						for (const preference of x) {
							if (this._preferences.has(preference.tileId)) {
								// In case it was already set by the frontend
								continue;
							}

							this._preferences.set(
								preference.tileId,
								preference.calendarId
							);
						}
					}
				);
		}

		return wrapPromiseWithCancellation(
			this._preferenceRetrievalPromise,
			signal
		);
	}
}

function convertToItemModel(
	appointment: AppointmentViewModel
): AppointmentItemModel {
	return {
		...appointment,
		uniqueId: isNotNil(appointment.id)
			? appointment.id
			: newGuid()
	};
}

export class CalendarInterestsMerger implements IInterestsMerger<CalendarResourceId, CalendarInterests>
{
	private readonly _interestsMap: Map<CalendarResourceId, CalendarInterests>;

	constructor(interestsMap: Map<CalendarResourceId, CalendarInterests>) {
		this._interestsMap = interestsMap;
	}

	public add(
		resourceId: CalendarResourceId,
		interestedIn: CalendarInterests
	): void {
		let interests: CalendarInterests | undefined = this._interestsMap.get(resourceId);
		if (!interests) {
			interests = {};
			this._interestsMap.set(resourceId, interests);
		}

		if (interestedIn.upcoming === true) {
			interests.upcoming = true;
		}

		if (interestedIn.dates) {
			interests.dates = union(interestedIn.dates, interests.dates ?? []);
		}
	}

	public entries(): readonly [CalendarResourceId, CalendarInterests][] {
		return [...this._interestsMap.entries()];
	}

	public hasAnyActualInterests(interests: CalendarInterests): boolean {
		return interests.upcoming || (interests.dates?.length ?? 0) > 0;
	}

	public computeMissingInterests(
		current: CalendarInterests,
		desired: CalendarInterests
	): CalendarInterests {
		const result: CalendarInterests = {};
		if (desired.upcoming && !current.upcoming) {
			result.upcoming = true;
		}

		if (desired.dates) {
			const missingDates: DateAsNumber[] = without(desired.dates, current.dates ?? []);
			if (missingDates.length > 0) {
				result.dates = missingDates;
			}
		}

		return result;
	};
}
